import { useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { CalendarCheck, CircleCheck, Hourglass, Search } from "lucide-react";
import {
  EmptyState,
  ErrorMessage,
  LoadingState,
  PageHeader,
  VDataTable,
  VInput,
  VKpiCard,
  VSelect,
  type VKpiCardProps,
} from "@/components/ui";
import type { Dossier, DossierStatus, ReservationRequest } from "@/v2/shared/model";
import { formatPeriod, fullName } from "@/v2/shared/format";
import { dossierStatusMeta } from "@/v2/shared/statusMeta";
import { DossierStatusBadge } from "@/v2/shared/statusBadges";
import { findV2UserById, V2_USERS } from "@/v2/auth/users";
import { useRequests } from "@/v2/data/queries";

const STATUS_OPTIONS: ReadonlyArray<DossierStatus> = ["ON_WAITLIST", "PLANNED", "CLOSED"];

// KPI-weergave per dossierstatus: icoon, kleurtoon en een korte toelichting,
// zodat de drie kaarten als één samenhangende set lezen (CB-13).
const kpiMeta: Record<
  DossierStatus,
  { icon: ReactNode; tone: NonNullable<VKpiCardProps["tone"]>; helper: string }
> = {
  ON_WAITLIST: {
    icon: <Hourglass className="size-4" />,
    tone: "warning",
    helper: "Nog geen inzet gepland",
  },
  PLANNED: {
    icon: <CalendarCheck className="size-4" />,
    tone: "success",
    helper: "Minstens één inzet gekoppeld",
  },
  CLOSED: {
    icon: <CircleCheck className="size-4" />,
    tone: "neutral",
    helper: "Camera-inzet beëindigd",
  },
};

const HANDLERS = V2_USERS.filter((u) => u.role === "manager");

function handlerName(handlerId: string | undefined): string {
  const handler = findV2UserById(handlerId ?? "");
  return handler ? fullName(handler.firstName, handler.lastName) : "-";
}

// Eén rij per dossier (Reservation): de toegekende periode onder een
// aanvraag, met het aantal inzetten en de status (CB-13).
type DossierRow = {
  dossier: Dossier;
  request: ReservationRequest;
};

export function DossierListPage(): React.JSX.Element {
  const navigate = useNavigate();
  const { data, isPending, isError } = useRequests();

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<DossierStatus | "all">("all");
  const [handler, setHandler] = useState<string | "all">("all");

  const allRows = useMemo<Array<DossierRow>>(
    () =>
      (data ?? []).flatMap((request) =>
        request.dossiers.map((dossier) => ({ dossier, request })),
      ),
    [data],
  );

  const counts = useMemo(() => {
    return STATUS_OPTIONS.map((s) => ({
      status: s,
      count: allRows.filter((row) => row.dossier.status === s).length,
    }));
  }, [allRows]);

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return allRows.filter(({ dossier, request }) => {
      const matchesStatus = status === "all" || dossier.status === status;
      const matchesHandler = handler === "all" || request.handlerId === handler;
      const matchesSearch =
        term === "" ||
        request.title.toLowerCase().includes(term) ||
        request.number.toLowerCase().includes(term) ||
        request.address.toLowerCase().includes(term);
      return matchesStatus && matchesHandler && matchesSearch;
    });
  }, [allRows, search, status, handler]);

  return (
    <div className="space-y-6">
      <PageHeader title="Dossiers" subtitle="Lopende en geplande camera-inzet." />

      {isPending ? (
        <LoadingState label="Dossiers laden…" />
      ) : isError ? (
        <ErrorMessage title="Kon de dossiers niet laden" />
      ) : (
        <>
          {/* CB-12/CB-13: KPI's per status; de wachtrij springt eruit en is
              via de statusfilter apart raadpleegbaar. */}
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
            {counts.map((c) => {
              const meta = kpiMeta[c.status];
              return (
                <VKpiCard
                  key={c.status}
                  label={dossierStatusMeta[c.status].label}
                  value={c.count}
                  helper={meta.helper}
                  tone={meta.tone}
                  icon={meta.icon}
                />
              );
            })}
          </div>

          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-56">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <VInput
                className="pl-9"
                placeholder="Zoek op aanvraagnummer, titel of adres…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <VSelect
              aria-label="Filter op status"
              className="w-48"
              value={status}
              onChange={(e) => {
                const value = e.target.value;
                setStatus(value === "all" ? "all" : (STATUS_OPTIONS.find((s) => s === value) ?? "all"));
              }}
            >
              <option value="all">Alle statussen</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {dossierStatusMeta[s].label}
                </option>
              ))}
            </VSelect>
            <VSelect
              aria-label="Filter op behandelaar"
              className="w-48"
              value={handler}
              onChange={(e) => setHandler(e.target.value)}
            >
              <option value="all">Alle behandelaars</option>
              {HANDLERS.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.firstName} {h.lastName}
                </option>
              ))}
            </VSelect>
          </div>

          <VDataTable<DossierRow>
            rows={rows}
            getRowKey={(row) => row.dossier.id}
            onRowClick={(row) => navigate(`/dossiers/${row.dossier.id}`)}
            emptyState={
              <EmptyState title="Geen dossiers gevonden" description="Pas je filters aan." />
            }
            columns={[
              {
                key: "number",
                header: "Aanvraag",
                cell: (row) => row.request.number,
                className: "whitespace-nowrap tabular-nums text-muted-foreground",
              },
              {
                key: "title",
                header: "Titel",
                cell: (row) => (
                  <span className="block">
                    <span className="font-medium text-foreground">{row.request.title}</span>
                    <span className="block text-xs text-muted-foreground">
                      {row.request.address}
                    </span>
                  </span>
                ),
              },
              {
                key: "period",
                header: "Toegekende periode",
                cell: (row) => formatPeriod(row.dossier.startDate, row.dossier.endDate),
                className: "whitespace-nowrap tabular-nums",
              },
              {
                key: "deployments",
                header: "Inzetten",
                align: "center",
                cell: (row) => row.dossier.deployments.length,
              },
              {
                key: "extension",
                header: "Verlenging",
                align: "center",
                cell: (row) => `${row.dossier.extensionCount}/2`,
                className: "whitespace-nowrap tabular-nums",
              },
              {
                key: "handler",
                header: "Behandelaar",
                cell: (row) => handlerName(row.request.handlerId),
              },
              {
                key: "status",
                header: "Status",
                cell: (row) => <DossierStatusBadge status={row.dossier.status} />,
              },
            ]}
          />
        </>
      )}
    </div>
  );
}
